/**
 * 整改内容提交页面
 * - 邮件/历史：?standalone=rectification-reply&id=xxx&email=xxx
 * - 统一整改中心（飞书外链）：?standalone=rectification-reply&source=unified&id=xxx&token=xxx
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { CheckCircle, Send, AlertCircle, Loader2, Paperclip } from 'lucide-react';

const MAX_FEEDBACK_FILES = 5;
const MAX_FEEDBACK_FILE_BYTES = 12 * 1024 * 1024;

export default function RectificationReplyStandalone() {
  const [rectId, setRectId] = useState('');
  const [token, setToken] = useState('');
  const [email, setEmail] = useState('');
  const [reporterName, setReporterName] = useState('');
  const [content, setContent] = useState('');
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [rectInfo, setRectInfo] = useState(null);
  const [unifiedMode, setUnifiedMode] = useState(false);

  const base = typeof window !== 'undefined' ? (window.__BASE_URL__ || '') : '';

  const loadUnifiedInfo = useCallback(async (id, tok) => {
    const res = await fetch(
      `${base}/api/public/unified-rectification/${encodeURIComponent(id)}?token=${encodeURIComponent(tok)}`,
    );
    const json = await res.json().catch(() => ({}));
    if (!res.ok || !json.success) {
      throw new Error(json.message || '无法加载整改信息');
    }
    setRectInfo(json.item);
  }, [base]);

  const loadLegacyRectInfo = useCallback(async (id) => {
    try {
      const res = await fetch(`${base}/api/pxcharts/rectification/${id}`);
      if (res.ok) {
        const data = await res.json();
        setRectInfo(data);
      }
    } catch (e) {
      console.error('加载整改信息失败:', e);
    }
  }, [base]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('id');
    const tok = params.get('token');
    const em = params.get('email');
    const source = params.get('source');
    if (id) setRectId(id);
    if (tok) setToken(tok);
    if (em) setEmail(decodeURIComponent(em));

    const isUnified = source === 'unified' || (!!id && !!tok);
    setUnifiedMode(isUnified);

    if (id && isUnified && tok) {
      loadUnifiedInfo(id, tok).catch((e) => {
        setError(e?.message || '加载失败');
      });
    } else if (id && !isUnified) {
      loadLegacyRectInfo(id);
    }
  }, [loadUnifiedInfo, loadLegacyRectInfo]);

  const onPickAttachmentFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    if (!unifiedMode) return;
    if (picked.length > MAX_FEEDBACK_FILES) {
      setError(`最多选择 ${MAX_FEEDBACK_FILES} 个附件`);
      return;
    }
    for (const f of picked) {
      if (f.size > MAX_FEEDBACK_FILE_BYTES) {
        setError(`「${f.name}」超过 12MB 上限`);
        return;
      }
    }
    setError('');
    setAttachmentFiles(picked);
  };

  const handleSubmit = async () => {
    const text = content.trim();
    if (!rectId || !text) {
      setError(unifiedMode ? '请填写整改说明' : '请填写完整的整改内容');
      return;
    }
    if (!unifiedMode && !email) {
      setError('请填写邮箱');
      return;
    }
    if (unifiedMode && !token) {
      setError('链接缺少安全参数，请从飞书通知或系统内复制的链接打开');
      return;
    }

    setLoading(true);
    setError('');

    try {
      if (unifiedMode) {
        const fd = new FormData();
        fd.append('token', token);
        fd.append('content', text);
        const rn = reporterName.trim();
        if (rn) fd.append('reporterName', rn);
        attachmentFiles.forEach((f) => fd.append('files', f));
        const res = await fetch(`${base}/api/public/unified-rectification/${encodeURIComponent(rectId)}/feedback`, {
          method: 'POST',
          body: fd,
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok || !json.success) {
          throw new Error(json.message || '提交失败');
        }
      } else {
        const res = await fetch(`${base}/api/email/rectification-reply`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            rectId,
            email,
            content: text,
          }),
        });
        if (!res.ok) {
          throw new Error('提交失败');
        }
      }

      setSubmitted(true);
    } catch (e) {
      console.error('提交整改内容失败:', e);
      setError(e?.message || '提交失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">提交成功</h1>
          <p className="text-gray-600 mb-6">
            您的整改说明已同步至审计管理系统整改台账，感谢您的配合。
          </p>
          <a
            href="/"
            className="inline-block px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            返回首页
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100 py-8 px-4">
      <div className="max-w-xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4">
            <h1 className="text-xl font-bold text-white flex items-center gap-2">
              <Send className="w-6 h-6" />
              {unifiedMode ? '更新整改进展' : '整改内容提交'}
            </h1>
            <p className="text-amber-100 text-sm mt-1">
              {unifiedMode ? '向审计系统回传最新整改情况（无需登录）' : '请在下方填写您的整改内容'}
            </p>
          </div>

          <div className="p-6 space-y-4">
            {rectInfo && (
              <div className="bg-amber-50 rounded-lg p-4 mb-4">
                <h3 className="font-semibold text-gray-800 mb-2">{rectInfo.title}</h3>
                <div className="text-sm text-gray-600 space-y-1">
                  {rectInfo.unit && <p>责任单位：{rectInfo.unit}</p>}
                  {rectInfo.responsible && <p>责任人：{rectInfo.responsible}</p>}
                  {rectInfo.deadline && <p>完成时限：{rectInfo.deadline}</p>}
                  {rectInfo.status && unifiedMode && <p>当前状态：{rectInfo.status}</p>}
                </div>
              </div>
            )}

            {unifiedMode && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  您的姓名（选填）
                </label>
                <input
                  type="text"
                  value={reporterName}
                  onChange={(e) => setReporterName(e.target.value)}
                  placeholder="便于审计端识别反馈人"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
              </div>
            )}

            {!unifiedMode && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  您的邮箱
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="请输入您的邮箱地址"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {unifiedMode ? '最新整改进展与说明' : '整改内容'}
              </label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder={
                  unifiedMode
                    ? '请说明当前整改进展、已完成措施、计划完成时间等…'
                    : '请详细描述您的整改措施和计划…'
                }
                rows={8}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
              />
              <p className="text-xs text-gray-500 mt-1">
                {unifiedMode
                  ? '提交后将追加为「整改进度」记录，并可能将状态从「待整改」自动置为「整改中」。'
                  : '请详细说明您的整改措施、计划和时间安排'}
              </p>
            </div>

            {unifiedMode && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">附件（选填）</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.webp,.zip,.txt"
                  onChange={onPickAttachmentFiles}
                />
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="inline-flex items-center gap-1.5 px-3 py-2 border border-amber-300 rounded-lg text-sm text-amber-900 bg-amber-50/80 hover:bg-amber-100"
                  >
                    <Paperclip className="w-4 h-4" />
                    选择文件
                  </button>
                  <span className="text-xs text-gray-500">
                    最多 {MAX_FEEDBACK_FILES} 个，单个 ≤12MB；保存至服务器磁盘，不占内存
                  </span>
                </div>
                {attachmentFiles.length > 0 ? (
                  <ul className="mt-2 text-xs text-gray-700 space-y-1">
                    {attachmentFiles.map((f, i) => (
                      <li key={`${f.name}-${i}`} className="flex items-center justify-between gap-2 bg-gray-50 rounded px-2 py-1">
                        <span className="truncate">{f.name}</span>
                        <button
                          type="button"
                          className="text-amber-700 shrink-0"
                          onClick={() => setAttachmentFiles((prev) => prev.filter((_, j) => j !== i))}
                        >
                          移除
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            )}

            {error && (
              <div className="flex items-center gap-2 text-red-600 bg-red-50 p-3 rounded-lg">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            <button
              type="button"
              onClick={handleSubmit}
              disabled={
                loading ||
                !rectId ||
                !content.trim() ||
                (!unifiedMode && !email) ||
                (unifiedMode && !token)
              }
              className="w-full py-3 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  提交中...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  {unifiedMode ? '提交整改进展' : '提交整改内容'}
                </>
              )}
            </button>

            <p className="text-xs text-gray-500 text-center">
              {unifiedMode ? '信息安全：请勿转发含 token 的完整链接给无关人员。' : '提交后系统将自动通知审计部门'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
